import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnDestroy
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { map, flatten, find, flow } from 'lodash/fp';
import { debounceTime, filter } from 'rxjs/operators';

import { DropdownItem, DropdownGroup, DropdownItemValue } from '@tripointe/tripointe-cms/types';
import { AutoUnsubscribe, takeWhileAlive } from '@tripointe/tripointe-cms/utils';

const GROUPED_AUTOCOMPLETE_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => GroupedAutocompleteComponent),
  multi: true
};

const getOptionBy = (fieldName: string) => (
  groupedOptions: DropdownGroup[],
  fieldValue: string
): DropdownItem => {
  return flow(map('options'), flatten, find({ [fieldName]: fieldValue }))(groupedOptions);
};

const getOptionByValue = getOptionBy('value');
const getOptionByText = getOptionBy('text');
const isString = (val: any) => typeof val === 'string';

@Component({
  selector: 'tp-grouped-autocomplete',
  templateUrl: './grouped-autocomplete.component.html',
  styleUrls: ['./grouped-autocomplete.component.scss'],
  providers: [GROUPED_AUTOCOMPLETE_VALUE_ACCESSOR]
})
@AutoUnsubscribe()
export class GroupedAutocompleteComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() placeholder = '';
  @Input() icon = 'search';
  @Input() optionsGroups: DropdownGroup[] = [];
  @Input() loading = false;
  @Output() queryChanged = new EventEmitter<string>();
  @Output() valueChange = new EventEmitter<DropdownItemValue>();

  selectedValue: DropdownItemValue = '';
  ctrl = new FormControl();
  onChanged = (_: any) => {};
  onTouched = () => {};

  ngOnInit(): void {
    this.ctrl.valueChanges
      .pipe(debounceTime(400), filter(isString), takeWhileAlive(this))
      .subscribe((ctrlValue: string) => {
        this.queryChanged.emit(ctrlValue);
      });
  }

  ngOnDestroy(): void {}

  writeValue(value: string): void {
    this.selectedValue = value;
    const selectedOption = getOptionByValue(this.optionsGroups, value);

    if (selectedOption) {
      this.ctrl.setValue(selectedOption);
    }
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  displayFn(option: DropdownItem): string {
    return option ? option.text : '';
  }

  onBlur(event: any): void {
    const isSelectOptionEvent = event.relatedTarget && event.relatedTarget.tagName === 'MAT-OPTION';

    if (isSelectOptionEvent) {
      return;
    }

    const query = this.getQuery(this.ctrl.value);
    const selectedOption = getOptionByText(this.optionsGroups, query);
    const hasSameValue = selectedOption && selectedOption.value === this.selectedValue;

    if (hasSameValue) {
      return;
    }

    this.handleValueChange(selectedOption ? selectedOption.value : '');
    this.onTouched();
  }

  onOptionSelected(selectedEvent: MatAutocompleteSelectedEvent): void {
    const selectedOption: DropdownItem = selectedEvent.option.value;
    this.handleValueChange(selectedOption.value);
  }

  handleValueChange(value: DropdownItemValue): void {
    this.onChanged(value);
    this.valueChange.emit(value);
    this.selectedValue = value;
  }

  getQuery(ctrlValue: string | DropdownItem): string {
    if (typeof ctrlValue === 'string') {
      return ctrlValue.trim();
    }

    return ctrlValue?.text.trim();
  }
}
